import ErrorBoundary from '@/components/ErrorBoundary';
import { Loading } from '@/components/loaders';
import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { FlowMappingsForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FlowMappingsForm';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData, FlowMappingsConfig } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { useSmsAliasConnector } from '@/features/campaigns/v2/Sms/hooks/useSmsAliasConnector';
import { RouteConstants } from '@/features/router/RouteConstants';
import { showToast } from '@/features/toasts/toastsSlice';
import { StepPageActions } from '@/global/components/Layout/StepPageActions';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { useWorkspace } from '@/features/workspaces/hooks/useWorkspace';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers/CompanyProvider';
import { useAppDispatch } from '@/reducers';
import { PrepareConnectorFlowStepResponse, useCreateFlowInstanceMutation, usePrepareConnectorFlowStepMutation } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

type CreateSenderOpts = {
  flowParams?: FlowFormData;
  flowMappings?: FlowFormData;
  fromFlowComplete?: boolean;
};

export const SmsSenderInsertFlow = () => {
  const { id: workspaceId } = useWorkspace();
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const { insertFlowId, connectorInstanceId, isLoading: isConnectorLoading, missingInsertFlow } = useSmsAliasConnector();
  const [createFlowInstance] = useCreateFlowInstanceMutation();
  const appDispatch = useAppDispatch();
  const { t } = useTranslation('campaign');
  const { t: tAll } = useTranslation('ALL');
  const history = useHistory();

  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [formData, setFormData] = useState<FlowFormData>({});
  const [multiStepMappingsConfig, setMultiStepMappingsConfig] = useState<FlowMappingsConfig | null>(null);
  const [prepareStep] = usePrepareConnectorFlowStepMutation();
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);
  const [multiStepsCompleted, setMultiStepsCompleted] = useState(false);

  useEffect(() => {
    const fetchPrepareData = async () => {
      const response = await prepareStep({
        workspaceId,
        flowId: insertFlowId,
        audienceId: audienceId!,
        stepId: 'step1',
        stepData: {},
        connectorInstanceId,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);
      setPrepareResult(translatedResponse?.configuration?.elements?.params as PrepareStepResult);
    };
    if (!!connectorInstanceId && !!insertFlowId && !!audienceId) {
      void fetchPrepareData();
    }
  }, [audienceId, connectorInstanceId, insertFlowId, prepareStep, currentLanguage, workspaceId]);

  const handleCreateSender = async (opts?: CreateSenderOpts) => {
    const flowParams = opts?.flowParams ?? formData;
    const flowMappings = opts?.flowMappings ?? mappingData;

    const mappingsUiRequired = !!multiStepMappingsConfig?.schema?.properties && Object.keys(multiStepMappingsConfig.schema.properties).length > 0;
    if (!opts?.fromFlowComplete && mappingsUiRequired && !isMappingsFormValid) {
      return { success: false, message: 'Invalid mappings' };
    }

    if (!connectorInstanceId) {
      const message = String(t('smsWorkspace.senders.missingConnectorInstance'));
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    if (!audienceId) {
      const message = String(t('missingAudience.toast'));
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    if (!insertFlowId) {
      const message = String(t('smsWorkspace.senders.missingInsertFlow'));
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    const payload = buildFlowInstancePayload({
      workspaceId,
      connectorFlowId: insertFlowId,
      connectorInstanceId,
      audienceId,
      flowParams,
      flowMappings,
    });

    try {
      await createFlowInstance(payload).unwrap();
      appDispatch(showToast({ title: String(t('smsWorkspace.senders.createSuccess')), type: ToastType.SUCCESS }));
      history.push(RouteConstants.smsSenders);
      return { success: true };
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('smsWorkspace.senders.createFailed'));
      appDispatch(showToast({ type: ToastType.ERROR, title: errorMessage }));
      return { success: false, message: errorMessage };
    }
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON.cancel' as any,
    onClick: () => history.push(RouteConstants.smsSenders),
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON.confirm' as any,
    onClick: () => void handleCreateSender(),
    disabled: !isMappingsFormValid,
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const multiStepFlowFormProps = {
    id: 'sms-sender-insert-form',
    audienceId: audienceId!,
    connectorInstanceId: connectorInstanceId!,
    connectorFlowId: insertFlowId!,
    paramsConfig: prepareResult || {},
    onComplete: (allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => {
      setFormData(allParams);
      setMultiStepMappingsConfig(mappingsConfig);
      setMappingData({});
      setIsMappingsFormValid(false);
      setMultiStepsCompleted(true);
      const hasMappingsUi = !!mappingsConfig?.schema?.properties && Object.keys(mappingsConfig.schema.properties).length > 0;
      if (!hasMappingsUi) {
        void handleCreateSender({ flowParams: allParams, flowMappings: {}, fromFlowComplete: true });
      }
    },
  };

  const hasMappingsUi = !!multiStepMappingsConfig?.schema?.properties && Object.keys(multiStepMappingsConfig.schema.properties).length > 0;
  const isConnectorReady = !!connectorInstanceId && !!insertFlowId;
  const isPreparePending = hasAudience && isConnectorReady && !prepareResult;
  const isFormLoading = isAudienceLoading || isConnectorLoading || isPreparePending;

  if (isAudienceLoading || isConnectorLoading) {
    return (
      <div className='space-y-4'>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.senders.registerTitle')}</h2>
        <Loading />
      </div>
    );
  }

  if (isAudienceSettled && !hasAudience) {
    return (
      <div className='space-y-4'>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.senders.registerTitle')}</h2>
        <AudienceRequiredEmptyState />
      </div>
    );
  }

  if (missingInsertFlow) {
    return (
      <div className='space-y-4'>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.senders.registerTitle')}</h2>
        <p className='text-sm text-gray-600'>{t('smsWorkspace.senders.missingInsertFlow')}</p>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.senders.registerTitle')}</h2>
      {!multiStepsCompleted && (
        <ErrorBoundary>
          {prepareResult ? (
            <MultiStepFlowForm {...multiStepFlowFormProps} />
          ) : (
            <p className='text-sm text-gray-600'>
              {isFormLoading ? t('smsWorkspace.senders.flowLoadingMessage') : t('smsWorkspace.senders.failedToLoadFlow')}
            </p>
          )}
        </ErrorBoundary>
      )}
      {multiStepsCompleted && hasMappingsUi ? (
        <ErrorBoundary>
          <div>
            <h3 className='mb-4 text-lg font-medium'>{tAll('destinations.steps.configureFlow.dataMapping')}</h3>
            <FlowMappingsForm
              config={multiStepMappingsConfig}
              formData={mappingData}
              onFormDataChange={handleMappingDataChange}
              onValidationChange={handleMappingsValidationChange}
            />
            <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
          </div>
        </ErrorBoundary>
      ) : null}
    </div>
  );
};
